import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Trophy, Plus, Upload, Download, X, Search, ArrowLeft, Pencil, Trash2, MapPin, Calendar, AlertTriangle, RotateCcw, Lock, History, Droplets, Unlock } from 'lucide-react';
import api from '../../api/client';
import { conferma, avvisa, DialogoHost } from '../../components/Dialogo';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import useIsMobile from '../../utils/useIsMobile';

// Contabilità tornei amatoriali (09/10/2026): sostituisce l'Excel del titolare. Tipologie con
// prezzo standard/tesserato, tornei, una riga per partecipante con dovuto e pagamenti, import
// dell'export iscrizioni di Dink. Non scrive nei movimenti bancari: il soldo vero si vede in banca.
const METODI = [['SATISPAY', 'Satispay'], ['CONTANTI', 'Contanti'], ['BONIFICO', 'Bonifico']];
const NOME_METODO = Object.fromEntries(METODI);
const STATO = {
  DA_PAGARE:     { label: 'Da pagare',    col: '#B42318', bg: '#FEE4E2' },
  PARZIALE:      { label: 'Parziale',     col: '#B45309', bg: '#FEF3C7' },
  PAGATO:        { label: 'Pagato',       col: '#15803D', bg: '#DCFCE7' },
  NIENTE_DOVUTO: { label: 'Niente dovuto', col: '#6E6E73', bg: '#F2F2F5' },
  STAFF:         { label: 'Staff · omaggio', col: '#6D28D9', bg: '#EDE9FE' },
};
const MESI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const eur = (n) => (n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: (n || 0) % 1 ? 2 : 0 });
const gg = (iso) => { if (!iso) return ''; const [a, m, d] = iso.split('-'); return `${+d} ${MESI[+m - 1]} ${a}`; };
const oggi = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(new Date());
const errore = (e, def) => e?.response?.data?.error || def;
// filtri a tendina combinabili (12/10, richiesta titolare: "più filtri meglio è"). Ogni filtro è
// { k, label, opzioni: [[valore, etichetta, test(riga)]] }; valore '' = tutti. Il numero tra
// parentesi conta le righe che passano anche gli ALTRI filtri attivi, così si vede cosa resta.
const applicaFiltri = (righe, filtri, scelti, salta) => righe.filter(r => filtri.every(fl => {
  if (fl.k === salta || !scelti[fl.k]) return true;
  const o = fl.opzioni.find(x => x[0] === scelti[fl.k]);
  return !o || o[2](r);
}));
function Filtri({ filtri, scelti, setScelti, righe }) {
  const attivi = filtri.filter(fl => scelti[fl.k]).length;
  return (
    <div style={s.filtriGriglia}>
      {filtri.map(fl => {
        const base = applicaFiltri(righe, filtri, scelti, fl.k);
        // voci senza nessuno nascoste (riappaiono quando servono); filtro inutile se resta una sola voce che prende tutti
        const voci = fl.opzioni.map(([v, l, t]) => [v, l, base.filter(t).length]).filter(([v, , n]) => n > 0 || v === scelti[fl.k]);
        if (!scelti[fl.k] && (voci.length === 0 || (voci.length === 1 && voci[0][2] === base.length))) return null;
        return (
          <label key={fl.k} style={{ minWidth: 0 }}>
            <span style={s.labFiltro}>{fl.label}</span>
            <select style={{ ...s.input, padding: '8px 10px', fontSize: '14px', ...(scelti[fl.k] ? s.selectOn : {}) }} value={scelti[fl.k] || ''} onChange={e => setScelti({ ...scelti, [fl.k]: e.target.value })}>
              <option value="">Tutti ({base.length})</option>
              {voci.map(([v, l, n]) => <option key={v} value={v}>{l} ({n})</option>)}
            </select>
          </label>
        );
      })}
      {attivi > 0 && <button style={{ ...s.secondario, alignSelf: 'end', justifyContent: 'center' }} onClick={() => setScelti({})}><RotateCcw size={14} /> Azzera filtri ({attivi})</button>}
    </div>
  );
}
const distinti = (valori) => [...new Set(valori.filter(v => v !== null && v !== undefined && v !== ''))].sort((a, b) => String(a).localeCompare(String(b), 'it'));
// stagione sportiva 1/9–31/8 (CLAUDE.md 6-quinquies)
const stagioneDi = (iso) => { if (!iso) return null; const [a, m] = iso.split('-').map(Number); const y = m >= 9 ? a : a - 1; return `${y}/${y + 1}`; };

const Badge = ({ stato }) => <span style={{ ...s.badge, color: STATO[stato]?.col, background: STATO[stato]?.bg }}>{STATO[stato]?.label}</span>;

function Finestra({ titolo, children, onChiudi, largo }) {
  useEffect(() => {
    const k = (e) => e.key === 'Escape' && onChiudi();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onChiudi]);
  return createPortal(
    <div style={s.velo}>
      <div style={{ ...s.finestra, maxWidth: largo ? '760px' : '560px' }}>
        <button style={{ ...s.xBtn, position: 'absolute', top: '16px', right: '16px' }} onClick={onChiudi} aria-label="Chiudi"><X size={18} /></button>
        <h2 style={s.h2}>{titolo}</h2>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ── tipologie ────────────────────────────────────────────────────────────────────────────
function Tipologie({ onCambio }) {
  const { user } = useAuth();
  const [lista, setLista] = useState(null);
  const [form, setForm] = useState(null); // { id?, nome, prezzo_standard, prezzo_tesserato, attiva }
  const [err, setErr] = useState('');
  const carica = () => api.get('/tornei/tipologie').then(r => setLista(r.data)).catch(e => setErr(errore(e, 'Impossibile caricare')));
  useEffect(() => { carica(); }, []);

  const salva = async () => {
    setErr('');
    try {
      if (form.id) await api.put(`/tornei/tipologie/${form.id}`, form);
      else await api.post('/tornei/tipologie', form);
      setForm(null); carica(); onCambio();
    } catch (e) { setErr(errore(e, 'Salvataggio non riuscito')); }
  };
  const elimina = async (t) => {
    if (!window.confirm(`Eliminare la tipologia "${t.nome}"?`)) return;
    try { await api.delete(`/tornei/tipologie/${t.id}`); carica(); onCambio(); } catch (e) { alert(errore(e, 'Eliminazione non riuscita')); }
  };

  return (
    <div>
      <p style={s.sub}>Le tipologie sono i "modelli" dei tornei (B-Easy, Season…): il prezzo a persona viene copiato nel torneo quando lo crei, così cambiarlo qui non tocca i tornei già fatti.</p>
      {puoFare(user, 'tornei', 'create') && !form && <button style={s.primario} onClick={() => setForm({ nome: '', prezzo_standard: '', prezzo_tesserato: '', sconto_tesserati: true })}><Plus size={16} /> Nuova tipologia</button>}
      {form && (
        <div style={{ ...s.box, maxWidth: '560px' }}>
          <label style={s.lab}>Nome</label>
          <input style={s.input} value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="es. B-Easy" autoFocus />
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}><label style={s.lab}>Prezzo standard (€)</label><input style={s.input} inputMode="decimal" value={form.prezzo_standard} onChange={e => setForm({ ...form, prezzo_standard: e.target.value })} /></div>
            <div style={{ flex: 1 }}><label style={s.lab}>Prezzo tesserato (€)</label><input style={s.input} inputMode="decimal" value={form.prezzo_tesserato} onChange={e => setForm({ ...form, prezzo_tesserato: e.target.value })} /></div>
          </div>
          <SpuntaSconto valore={form.sconto_tesserati} onCambio={v => setForm({ ...form, sconto_tesserati: v })} />
          {form.id && <label style={{ ...s.lab, display: 'flex', gap: '8px', alignItems: 'center' }}><input type="checkbox" checked={form.attiva} onChange={e => setForm({ ...form, attiva: e.target.checked })} /> Attiva (si può scegliere per i tornei nuovi)</label>}
          {err && <div style={s.err}>{err}</div>}
          <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
            <button style={s.primario} onClick={salva}>Salva</button>
            <button style={s.secondario} onClick={() => { setForm(null); setErr(''); }}>Annulla</button>
          </div>
        </div>
      )}
      {!lista ? <div style={s.vuoto}>Caricamento…</div> : lista.length === 0 ? <div style={s.vuoto}>Nessuna tipologia: creane una per iniziare.</div> : (
        <div style={{ ...s.griglia, marginTop: '16px' }}>
          {lista.map(t => (
            <div key={t.id} style={{ ...s.card, cursor: 'default', opacity: t.attiva ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ fontWeight: 700, fontSize: '16px' }}>{t.nome}</div>
                {!t.attiva && <span style={{ ...s.badge, background: '#F2F2F5', color: '#6E6E73' }}>Disattivata</span>}
              </div>
              <div style={{ fontSize: '14px', marginTop: '6px' }}>{t.sconto_tesserati ? <>Standard <b>{eur(t.prezzo_standard)}</b> · Tesserato <b>{eur(t.prezzo_tesserato)}</b></> : <>Prezzo unico <b>{eur(t.prezzo_standard)}</b> · <span style={s.senzaSconto}>senza sconto tesserati</span></>}</div>
              <div style={s.nota}>{t._count.tornei} tornei</div>
              <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                {puoFare(user, 'tornei', 'edit') && <button style={s.mini} onClick={() => setForm({ id: t.id, nome: t.nome, prezzo_standard: String(t.prezzo_standard), prezzo_tesserato: String(t.prezzo_tesserato), sconto_tesserati: t.sconto_tesserati, attiva: t.attiva })}><Pencil size={13} /> Modifica</button>}
                {puoFare(user, 'tornei', 'delete') && t._count.tornei === 0 && <button style={s.miniNo} onClick={() => elimina(t)}><Trash2 size={13} /> Elimina</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 23/10/2026 (titolare): non tutti i tornei hanno lo sconto (es. i federali) → spento = tutti prezzo standard
function SpuntaSconto({ valore, onCambio }) {
  return (
    <label style={{ ...s.lab, display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
      <input type="checkbox" checked={valore !== false} onChange={e => onCambio(e.target.checked)} style={{ marginTop: '3px' }} />
      <span>Sconto tesserati<span style={{ ...s.nota, display: 'block', fontWeight: 400 }}>{valore !== false ? 'Chi ha diritto paga il prezzo tesserato.' : 'Spento: tutti pagano il prezzo standard (es. tornei federali).'}</span></span>
    </label>
  );
}

// ── nuovo / modifica torneo ──────────────────────────────────────────────────────────────
// Acqua (30/10/2026): inclusa sì/no e costo totale, che finisce nelle spese del torneo
function CampiAcqua({ f, setF }) {
  return (
    <div style={{ marginTop: '10px' }}>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px', fontWeight: 600 }}><input type="checkbox" checked={!!f.acqua_inclusa} onChange={e => setF({ ...f, acqua_inclusa: e.target.checked })} /> Acqua inclusa</label>
      {f.acqua_inclusa && <>
        <label style={s.lab}>Quanto costa l'acqua in totale (€)</label>
        <input style={{ ...s.input, maxWidth: '200px' }} inputMode="decimal" value={f.acqua_costo} onChange={e => setF({ ...f, acqua_costo: e.target.value })} placeholder="es. 30" />
        <div style={s.nota}>Il totale comprato per la giornata: finisce nelle spese del torneo. Se non lo sai ancora, puoi scriverlo dopo.</div>
      </>}
    </div>
  );
}

// Consegna con conferma, poi bloccata: per sbloccare servono motivo e password, e resta nello storico
function BottoneAcqua({ p, puoEdit, puoSblocca, chiuso, onCambio }) {
  const [sblocca, setSblocca] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const consegna = async () => {
    if (!await conferma(`Acqua consegnata a ${p.cognome} ${p.nome}?\nSei sicuro? Dopo resta bloccata: per sbloccarla servono autorizzazione e motivo.`, { si: 'Sì, consegnata' })) return;
    try { await api.post(`/tornei/partecipanti/${p.id}/acqua`); onCambio(); }
    catch (e) { await avvisa(errore(e, 'Consegna non registrata')); onCambio(); }
  };
  const invia = async () => {
    setErr('');
    try { await api.post(`/tornei/partecipanti/${p.id}/acqua/sblocca`, { motivo, password }); setSblocca(false); setMotivo(''); setPassword(''); onCambio(); }
    catch (e) { setErr(errore(e, 'Sblocco non riuscito')); }
  };
  if (!p.acqua_consegnata) return puoEdit && !chiuso
    ? <button style={s.secondario} onClick={consegna}><Droplets size={14} /> Consegna</button>
    : <span style={s.nota}>Da consegnare</span>;
  const chi = `${p.acqua_consegnata_da || ''}${p.acqua_consegnata_at ? ` · ${new Date(p.acqua_consegnata_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}` : ''}`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <span style={{ ...s.badge, color: '#1D4ED8', background: '#EFF6FF' }} title={chi}><Lock size={11} /> Consegnata</span>
      {puoSblocca && !chiuso && <button style={{ ...s.xBtn, marginLeft: '6px', verticalAlign: 'middle' }} title="Sblocca" aria-label="Sblocca" onClick={() => setSblocca(true)}><Unlock size={13} /></button>}
      {sblocca && (
        <Finestra titolo="Sblocca acqua" onChiudi={() => setSblocca(false)}>
          <p style={{ ...s.sub, marginBottom: '8px' }}><b>{p.cognome} {p.nome}</b>: consegnata da {chi}. Lo sblocco resta nello storico del torneo.</p>
          <label style={s.lab}>Motivo</label>
          <input style={s.input} value={motivo} autoFocus onChange={e => setMotivo(e.target.value)} placeholder="es. segnata alla persona sbagliata" />
          <label style={s.lab}>La tua password (autorizzazione)</label>
          <input style={s.input} type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} />
          {err && <div style={s.err}>{err}</div>}
          <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
            <button style={s.primario} disabled={motivo.trim().length < 5 || !password} onClick={invia}>Sblocca</button>
            <button style={s.secondario} onClick={() => setSblocca(false)}>Annulla</button>
          </div>
        </Finestra>
      )}
    </span>
  );
}

function FormTorneo({ torneo, onChiudi, onSalvato }) {
  const [tipologie, setTipologie] = useState([]);
  const [f, setF] = useState(torneo
    ? { nome: torneo.nome, data: torneo.data || '', luogo: torneo.luogo || '', note: torneo.note || '', prezzo_standard: String(torneo.prezzo_standard), prezzo_tesserato: String(torneo.prezzo_tesserato), sconto_tesserati: torneo.sconto_tesserati, acqua_inclusa: !!torneo.acqua_inclusa, acqua_costo: torneo.acqua_costo ?? '' }
    : { tipologia_id: '', nome: '', data: '', luogo: '', note: '', prezzo_standard: '', prezzo_tesserato: '', sconto_tesserati: true, acqua_inclusa: false, acqua_costo: '' });
  const [err, setErr] = useState('');
  useEffect(() => { if (!torneo) api.get('/tornei/tipologie').then(r => setTipologie(r.data.filter(t => t.attiva))); }, [torneo]);

  const scegliTipologia = (id) => {
    const t = tipologie.find(x => x.id === +id);
    setF({ ...f, tipologia_id: id, prezzo_standard: t ? String(t.prezzo_standard) : '', prezzo_tesserato: t ? String(t.prezzo_tesserato) : '', sconto_tesserati: t ? t.sconto_tesserati : true });
  };
  const salva = async () => {
    setErr('');
    try {
      const r = torneo ? await api.put(`/tornei/${torneo.id}`, f) : await api.post('/tornei', f);
      onSalvato(r.data.id);
    } catch (e) { setErr(errore(e, 'Salvataggio non riuscito')); }
  };

  return (
    <Finestra titolo={torneo ? 'Modifica torneo' : 'Nuovo torneo'} onChiudi={onChiudi}>
      {!torneo && (<>
        <label style={s.lab}>Tipologia</label>
        {tipologie.length === 0 ? <div style={s.nota}>Prima crea una tipologia nella scheda "Tipologie".</div> : (
          <select style={s.input} value={f.tipologia_id} onChange={e => scegliTipologia(e.target.value)}>
            <option value="">Scegli…</option>
            {tipologie.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        )}
      </>)}
      <label style={s.lab}>Nome del torneo</label>
      <input style={s.input} value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} placeholder="es. B-Easy Vol. 1" />
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px' }}><label style={s.lab}>Data</label><input type="date" style={s.input} value={f.data} onChange={e => setF({ ...f, data: e.target.value })} /></div>
        <div style={{ flex: '2 1 200px' }}><label style={s.lab}>Luogo</label><input style={s.input} value={f.luogo} onChange={e => setF({ ...f, luogo: e.target.value })} /></div>
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <div style={{ flex: 1 }}><label style={s.lab}>Prezzo standard (€)</label><input style={s.input} inputMode="decimal" value={f.prezzo_standard} onChange={e => setF({ ...f, prezzo_standard: e.target.value })} /></div>
        <div style={{ flex: 1 }}><label style={s.lab}>Prezzo tesserato (€)</label><input style={s.input} inputMode="decimal" value={f.prezzo_tesserato} onChange={e => setF({ ...f, prezzo_tesserato: e.target.value })} /></div>
      </div>
      <SpuntaSconto valore={f.sconto_tesserati} onCambio={v => setF({ ...f, sconto_tesserati: v })} />
      <CampiAcqua f={f} setF={setF} />
      <div style={s.nota}>{torneo ? 'Cambiare un prezzo o lo sconto ricalcola il dovuto di tutti, tranne chi ha un importo modificato a mano.' : 'Presi dalla tipologia: puoi cambiarli solo per questo torneo.'}</div>
      <label style={s.lab}>Note</label>
      <textarea style={{ ...s.input, minHeight: '70px' }} value={f.note} onChange={e => setF({ ...f, note: e.target.value })} />
      {err && <div style={s.err}>{err}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={s.primario} onClick={salva}>{torneo ? 'Salva' : 'Crea torneo'}</button>
        <button style={s.secondario} onClick={onChiudi}>Annulla</button>
      </div>
    </Finestra>
  );
}

// ── da liste FIPAV (30/10/2026, titolare): si scelgono i tornei, si controlla l'anteprima, si crea.
// Poi la contabilità vive da sola: le liste non vengono più rilette.
function DaTorneoFipav({ onChiudi, onCreato }) {
  const [dati, setDati] = useState(null);
  const [tutti, setTutti] = useState(false);
  const [q, setQ] = useState('');
  const [scelti, setScelti] = useState([]);
  const [riserve, setRiserve] = useState(false);
  const [ant, setAnt] = useState(null);
  const [f, setF] = useState({ nome: '', data: '', luogo: '', prezzo: '', note: '', acqua_inclusa: false, acqua_costo: '' });
  const [err, setErr] = useState('');
  const [lavoro, setLavoro] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      api.get('/tornei/fipav/disponibili', { params: { tutti: tutti ? '1' : undefined, q: q.trim() || undefined } })
        .then(r => { setDati(r.data); setF(x => ({ ...x, prezzo: x.prezzo || (r.data.prezzo_predefinito ?? '') })); })
        .catch(e => setErr(errore(e, 'Tornei FIPAV non caricati')));
    }, q ? 350 : 0);
    return () => clearTimeout(timer);
  }, [tutti, q]);

  const inversa = (id) => setScelti(l => l.includes(id) ? l.filter(x => x !== id) : [...l, id]);
  const anteprima = async (conRiserve = riserve) => {
    setErr(''); setLavoro(true);
    try {
      const r = await api.post('/tornei/fipav/crea', { ids: scelti, riserve: conRiserve, simula: true });
      setAnt(r.data);
      setF(x => ({ ...x, nome: x.nome || r.data.nome, data: x.data || r.data.data, luogo: x.luogo || r.data.luogo || '' }));
    } catch (e) { setErr(errore(e, 'Anteprima non riuscita')); }
    finally { setLavoro(false); }
  };
  const crea = async () => {
    setErr(''); setLavoro(true);
    try { const r = await api.post('/tornei/fipav/crea', { ids: scelti, riserve, ...f }); onCreato(r.data.id); }
    catch (e) { setErr(errore(e, 'Creazione non riuscita')); setLavoro(false); }
  };
  const posti = (t) => [t.main && `${t.main} Main Draw`, t.qualifiche && `${t.qualifiche} Qualifiche`, t.riserve && `${t.riserve} Riserve`, t.altre && `${t.altre} altre`].filter(Boolean).join(' · ');

  if (ant) {
    const nonCollegati = ant.persone.filter(p => !p.in_anagrafica).length;
    const haRiserve = ant.tornei.some(t => t.riserve > 0);
    return (
      <Finestra titolo="Controlla e crea" onChiudi={onChiudi} largo>
        <div style={s.nota}>{ant.tornei.map(t => `${t.titolo} (${posti(t)})`).join(' — ')}</div>
        {ant.gia_in.length > 0 && <div style={s.promemoria}><AlertTriangle size={15} style={{ verticalAlign: '-2px' }} /> Già usati in: <b>{[...new Set(ant.gia_in.map(x => x.nome))].join(', ')}</b>. Puoi crearne comunque un altro.</div>}
        <label style={s.lab}>Nome del torneo</label>
        <input style={s.input} value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} />
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 150px' }}><label style={s.lab}>Data</label><input type="date" style={s.input} value={f.data} onChange={e => setF({ ...f, data: e.target.value })} /></div>
          <div style={{ flex: '2 1 200px' }}><label style={s.lab}>Luogo</label><input style={s.input} value={f.luogo} onChange={e => setF({ ...f, luogo: e.target.value })} /></div>
          <div style={{ flex: '1 1 130px' }}><label style={s.lab}>Prezzo a persona (€)</label><input style={s.input} inputMode="decimal" value={f.prezzo} onChange={e => setF({ ...f, prezzo: e.target.value })} /></div>
        </div>
        <CampiAcqua f={f} setF={setF} />
        {haRiserve && <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px', fontSize: '14px' }}><input type="checkbox" checked={riserve} disabled={lavoro} onChange={e => { setRiserve(e.target.checked); anteprima(e.target.checked); }} /> Includi anche le riserve</label>}
        <div style={{ ...s.etichetta, marginTop: '14px' }}>{ant.persone.length} PERSONE{nonCollegati ? ` · ${nonCollegati} NON TROVATE IN ANAGRAFICA` : ''}</div>
        <div style={{ maxHeight: '260px', overflowY: 'auto', marginTop: '6px', border: '1px solid var(--border2)', borderRadius: '10px' }}>
          {ant.persone.map((p, i) => (
            <div key={i} style={{ padding: '7px 10px', borderBottom: '1px solid var(--border2)', fontSize: '13.5px' }}>
              <b>{p.cognome} {p.nome}</b>{!p.in_anagrafica && <span style={{ ...s.badge, marginLeft: '6px', color: '#B45309', background: '#FFFBEB' }}>non in anagrafica</span>}{p.quote > 1 && <span style={{ ...s.badge, marginLeft: '6px', background: '#FFF1E8', color: '#C24B08' }}>{p.quote} quote</span>}
              <div style={s.nota}>{p.tabelloni}{p.squadra ? ` · ${p.squadra}` : ''}</div>
            </div>
          ))}
        </div>
        <div style={s.nota}>Le persone si prendono adesso, una volta sola: se la lista FIPAV cambia dopo, qui non cambia niente. Staff e importi diversi si segnano dopo, dalla scheda della persona.</div>
        {err && <div style={s.err}>{err}</div>}
        <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
          <button style={s.primario} disabled={lavoro} onClick={crea}>{lavoro ? 'Attendi…' : `Crea con ${ant.persone.length} persone`}</button>
          <button style={s.secondario} disabled={lavoro} onClick={() => setAnt(null)}><ArrowLeft size={14} /> Cambia tornei</button>
        </div>
      </Finestra>
    );
  }

  return (
    <Finestra titolo="Nuovo torneo da FIPAV" onChiudi={onChiudi} largo>
      <p style={{ ...s.sub, marginBottom: '10px' }}>Scegli uno o più tornei: le coppie della lista d'ingresso FIPAV diventano gli iscritti da far pagare, a prezzo unico.</p>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ ...s.cerca, flex: '1 1 220px' }}><Search size={16} color="var(--text-dim)" /><input style={s.cercaInput} placeholder="Cerca luogo, categoria, società…" value={q} onChange={e => setQ(e.target.value)} /></div>
        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '14px' }}><input type="checkbox" checked={tutti} onChange={e => setTutti(e.target.checked)} /> Anche di altre società</label>
      </div>
      <div style={{ maxHeight: '340px', overflowY: 'auto', marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {!dati ? <div style={s.vuoto}>Caricamento…</div> : dati.tornei.length === 0 ? <div style={s.vuoto}>Nessun torneo trovato.</div> : dati.tornei.map(t => {
          const conLista = t.main + t.qualifiche + t.riserve + t.altre > 0;
          return (
            <label key={t.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '9px 11px', border: `1px solid ${scelti.includes(t.id) ? '#F4600C' : 'var(--border2)'}`, borderRadius: '10px', cursor: conLista ? 'pointer' : 'default', opacity: conLista ? 1 : .55 }}>
              <input type="checkbox" style={{ marginTop: '3px' }} disabled={!conLista} checked={scelti.includes(t.id)} onChange={() => inversa(t.id)} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{t.titolo}</b> · {gg(t.data)} · {t.comune}
                <span style={{ ...s.nota, display: 'block' }}>{conLista ? posti(t) : 'Lista non ancora pubblicata'}{t.provvisoria ? ' · lista provvisoria' : ''}{tutti && t.club ? ` · ${t.club}` : ''}</span>
                {t.annullato_fipav && <span style={{ ...s.nota, display: 'block', color: '#B42318', fontWeight: 600 }}>FIPAV lo segnala come annullato</span>}
                {t.gia_in.length > 0 && <span style={{ ...s.nota, display: 'block', color: '#B45309' }}>Già in: {t.gia_in.map(x => x.nome).join(', ')}</span>}
              </span>
            </label>
          );
        })}
      </div>
      {err && <div style={s.err}>{err}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
        <button style={s.primario} disabled={!scelti.length || lavoro} onClick={() => anteprima()}>{lavoro ? 'Attendi…' : `Avanti${scelti.length ? ` (${scelti.length})` : ''}`}</button>
        <button style={s.secondario} onClick={onChiudi}>Annulla</button>
      </div>
    </Finestra>
  );
}

// ── import Dink ──────────────────────────────────────────────────────────────────────────
function ImportaDink({ torneoId, onChiudi, onFatto }) {
  const [testo, setTesto] = useState('');
  const [nomeFile, setNomeFile] = useState('');
  const [anteprima, setAnteprima] = useState(null);
  const [esito, setEsito] = useState(null);
  const [err, setErr] = useState('');
  const [lavoro, setLavoro] = useState(false);

  const leggiFile = (file) => {
    if (!file) return;
    setNomeFile(file.name); setAnteprima(null); setEsito(null); setErr('');
    const r = new FileReader();
    r.onload = () => setTesto(String(r.result || ''));
    r.readAsText(file, 'utf-8');
  };
  const invia = async (simula) => {
    setErr(''); setLavoro(true);
    try {
      const r = await api.post(`/tornei/${torneoId}/importa`, { testo, simula });
      if (simula) setAnteprima(r.data); else { setEsito(r.data); onFatto(); }
    } catch (e) { setErr(errore(e, 'Import non riuscito')); }
    setLavoro(false);
  };
  const r = esito || anteprima;
  const problemi = r ? r.righe.filter(x => x.esito === 'errore' || x.esito === 'doppione') : [];

  return (
    <Finestra titolo="Importa iscritti da Dink" onChiudi={onChiudi} largo>
      <p style={{ ...s.sub, marginBottom: '10px' }}>Su Dink apri l'evento → Iscrizioni → esporta il CSV e caricalo qui. Puoi ricaricarlo quando arrivano nuove iscrizioni: aggiunge i nuovi e aggiorna i dati degli altri, senza toccare tesserato, importi modificati e pagamenti.</p>
      <label style={{ ...s.secondario, display: 'inline-flex' }}><Upload size={15} /> {nomeFile || 'Scegli il file CSV'}<input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={e => leggiFile(e.target.files?.[0])} /></label>
      <div style={s.nota}>Il codice fiscale serve a riconoscere la stessa persona tra un import e l'altro. Una persona iscritta a più tabelloni paga una quota per tabellone (colonna "n tournaments"): puoi correggere dalla sua riga.</div>
      {err && <div style={s.err}>{err}</div>}
      {r && (
        <div style={s.box}>
          <div style={s.etichetta}>{esito ? 'IMPORT FATTO' : 'ANTEPRIMA (non ancora salvato)'}</div>
          <div style={{ fontSize: '15px' }}><b>{r.nuovi}</b> nuovi · <b>{r.aggiornati}</b> aggiornati · <b>{r.invariati}</b> già uguali{r.errori ? <> · <b style={{ color: '#B42318' }}>{r.errori}</b> scartati</> : null}</div>
          {problemi.length > 0 && <div style={{ marginTop: '10px' }}>{problemi.map(p => <div key={p.riga} style={{ fontSize: '13px', color: '#B42318' }}>Riga {p.riga} · {p.nome}: {p.dettaglio}</div>)}</div>}
          {r.righe.filter(x => x.esito === 'aggiornato' || (x.esito === 'nuovo' && x.dettaglio)).slice(0, 30).map(p => <div key={p.riga} style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{p.nome}: {p.esito === 'nuovo' ? 'nuovo, ' : ''}{p.dettaglio}</div>)}
          {r.non_nel_file.length > 0 && (
            <div style={{ ...s.avvisoBox, marginTop: '10px' }}>
              <AlertTriangle size={16} style={{ flexShrink: 0 }} />
              <div>Importati in passato ma non più nel file (forse si sono ritirati): <b>{r.non_nel_file.join(', ')}</b>. Non li tolgo io: se serve eliminali dalla lista.</div>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
        {!esito && <button style={s.secondario} disabled={!testo || lavoro} onClick={() => invia(true)}>Anteprima</button>}
        {!esito && <button style={s.primario} disabled={!anteprima || lavoro} onClick={() => invia(false)}>Importa</button>}
        {esito && <button style={s.primario} onClick={onChiudi}>Fatto</button>}
      </div>
    </Finestra>
  );
}

// ── scheda partecipante ──────────────────────────────────────────────────────────────────
function SchedaPartecipante({ p, torneo, onChiudi, onCambio }) {
  const { user } = useAuth();
  const chiuso = !!torneo.chiuso_at;
  const puoEdit = !chiuso && puoFare(user, 'tornei', 'edit'), puoDel = !chiuso && puoFare(user, 'tornei', 'delete');
  const [ana, setAna] = useState({ nome: p.nome, cognome: p.cognome, email: p.email || '', telefono: p.telefono || '', codice_fiscale: p.codice_fiscale || '', squadra: p.squadra || '', quote: String(p.quote), note: p.note || '' });
  const [imp, setImp] = useState(null); // { importo, motivo }
  const [pag, setPag] = useState({ importo: '', metodo: 'SATISPAY', data: oggi(), note: '' });
  const [err, setErr] = useState('');
  const azione = async (fn) => { setErr(''); try { await fn(); onCambio(); } catch (e) { setErr(errore(e, 'Operazione non riuscita')); } };
  const calcolato = p.prezzo_unitario * p.quote;

  return (
    <Finestra titolo={`${p.cognome} ${p.nome}`} onChiudi={onChiudi} largo>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Badge stato={p.stato} />
        {p.squadra && <span style={s.nota}>Squadra: {p.squadra}</span>}
      </div>
      {p.tabelloni && <div style={s.nota}>Tabelloni: {p.tabelloni}</div>}

      {/* dovuto */}
      <div style={s.box}>
        <div style={s.etichetta}>QUANTO DEVE</div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '22px', fontWeight: 800 }}>{eur(p.dovuto)}</span>
          <span style={s.nota}>pagato {eur(p.pagato)}{p.residuo > 0 ? ` · mancano ${eur(p.residuo)}` : ''}{p.eccedenza > 0 ? ` · ${eur(p.eccedenza)} in più` : ''}</span>
        </div>
        {!torneo.fonte_fipav && <>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px' }}>Tesserato:</span>
          {p.tessera_asi
            ? <span style={{ ...s.badge, color: '#1D4ED8', background: '#EFF6FF' }}>Sì · {p.tessera_asi}{!chiuso ? (p.tesserato_fonte === 'CORSO' ? ' (dal planning corsi)' : p.tesserato_fonte === 'AIBVC' ? ' (iscritto AIBVC)' : ' (dall\'anagrafica)') : ''}</span>
            : <span style={{ ...s.badge, color: p.tesserato ? '#067647' : '#B42318', background: p.tesserato ? '#ECFDF3' : '#FEF3F2' }}>{p.tesserato ? 'Sì (a mano)' : 'No, nessuna tessera ASI'}</span>}
        </div>
        {torneo.sconto_tesserati === false && <div style={s.nota}>Questo torneo è senza sconto tesserati: paga comunque il prezzo standard.</div>}
        {p.b_senza_corso && <div style={{ ...s.nota, color: '#B45309', fontWeight: 600 }}>Ha la tessera ASI B ma non è nel planning corsi né iscritto AIBVC di questa stagione: niente sconto.</div>}
        {p.verifica_b?.length > 0 && <div style={{ ...s.nota, color: '#B45309' }}><b>Sconto dato perché {p.tesserato_fonte === 'AIBVC' ? 'è iscritto AIBVC' : 'è nel planning corsi'}, ma la tessera ASI B non risulta.</b> Possibili cause: {p.verifica_b.join('; ')}.</div>}
        {p.da_tesserare && <div style={{ ...s.nota, color: '#B45309', fontWeight: 600 }}>Da tesserare ASI A: non ha ASI A, non è nel planning corsi né iscritto AIBVC.</div>}
        {p.tessera_errata && <div style={{ ...s.nota, color: '#B45309' }}>Ha solo la tessera ASI con scadenza 31/12/2026 (errore di ASI, doveva scadere il 31/08/2026): nei tornei non vale, e nel planning corsi di questa stagione non risulta.</div>}
        {!p.tessera_asi && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px' }}>Tesserato a mano:</span>
            {[true, false].map(v => <button key={String(v)} disabled={!puoEdit} style={{ ...s.pill, ...(p.tesserato_manuale === v ? s.pillOn : {}) }} onClick={() => azione(() => api.put(`/tornei/partecipanti/${p.id}`, { tesserato: v }))}>{v ? 'Sì' : 'No'}</button>)}
            <span style={s.nota}>solo se la tessera c'è ma non è ancora nel gestionale{!p.codice_fiscale ? ' · senza codice fiscale non posso cercarla in anagrafica' : ''}</span>
          </div>
        )}
        </>}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px' }}>Staff (omaggio, non paga):</span>
          {[true, false].map(v => <button key={String(v)} disabled={!puoEdit} style={{ ...s.pill, ...(p.staff === v ? s.pillOn : {}) }} onClick={() => azione(() => api.put(`/tornei/partecipanti/${p.id}`, { staff: v }))}>{v ? 'Sì' : 'No'}</button>)}
        </div>
        {p.staff && <div style={{ ...s.nota, color: '#6D28D9', fontWeight: 600 }}>Fa parte dello staff del torneo: dovuto 0 €. Togliendo "staff" torna al prezzo qui sotto.</div>}
        <div style={s.nota}>Prezzo calcolato: {eur(p.prezzo_unitario)} {p.tesserato ? '(tesserato)' : '(standard)'}{p.quote > 1 ? ` × ${p.quote} quote = ${eur(calcolato)}` : ''}</div>
        {p.importo_modificato !== null && (
          <div style={{ ...s.avvisoBox, marginTop: '10px' }}>
            <Pencil size={15} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>Importo modificato a <b>{eur(p.importo_modificato)}</b> invece di {eur(calcolato)}. Motivo: <i>{p.motivo_modifica}</i><div style={s.nota}>{p.modificato_da}{p.modificato_at ? `, ${new Date(p.modificato_at).toLocaleDateString('it-IT')}` : ''}</div></div>
          </div>
        )}
        {puoEdit && !imp && !p.staff && (
          <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
            <button style={s.mini} onClick={() => setImp({ importo: String(p.importo_modificato ?? calcolato), motivo: '' })}><Pencil size={13} /> {p.importo_modificato !== null ? 'Cambia importo' : 'Modifica importo (sconto, omaggio…)'}</button>
            {p.importo_modificato !== null && <button style={s.mini} onClick={() => azione(() => api.put(`/tornei/partecipanti/${p.id}/importo`, { importo: null }))}><RotateCcw size={13} /> Torna al prezzo calcolato</button>}
          </div>
        )}
        {imp && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 120px' }}><label style={s.lab}>Nuovo importo €</label><input style={s.input} inputMode="decimal" value={imp.importo} onChange={e => setImp({ ...imp, importo: e.target.value })} /></div>
              <div style={{ flex: '1 1 220px' }}><label style={s.lab}>Motivo (obbligatorio)</label><input style={s.input} value={imp.motivo} placeholder="es. sconto staff, omaggio organizzatore" onChange={e => setImp({ ...imp, motivo: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <button style={s.primario} onClick={() => azione(async () => { await api.put(`/tornei/partecipanti/${p.id}/importo`, imp); setImp(null); })}>Salva importo</button>
              <button style={s.secondario} onClick={() => setImp(null)}>Annulla</button>
            </div>
          </div>
        )}
      </div>

      {/* pagamenti */}
      <div style={s.box}>
        <div style={s.etichetta}>PAGAMENTI</div>
        {p.pagamenti.length === 0 ? <div style={s.nota}>Nessun pagamento registrato.</div> : p.pagamenti.map(x => (
          <div key={x.id} style={s.rigaPag}>
            <b>{eur(x.importo)}</b> <span>{NOME_METODO[x.metodo]}</span> <span style={s.nota}>{gg(x.data)} · {x.registrato_da}{x.note ? ` · ${x.note}` : ''}</span>
            {puoDel && <button style={{ ...s.xBtn, marginLeft: 'auto', width: '28px', height: '28px' }} title="Elimina pagamento" onClick={() => window.confirm(`Eliminare il pagamento di ${eur(x.importo)}?`) && azione(() => api.delete(`/tornei/pagamenti/${x.id}`))}><Trash2 size={14} /></button>}
          </div>
        ))}
        {puoEdit && p.residuo > 0 && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '0 0 110px' }}><label style={s.lab}>Importo €</label><input style={s.input} inputMode="decimal" placeholder={String(p.residuo)} value={pag.importo} onChange={e => setPag({ ...pag, importo: e.target.value })} /></div>
              <div style={{ flex: '1 1 130px' }}><label style={s.lab}>Metodo</label><select style={s.input} value={pag.metodo} onChange={e => setPag({ ...pag, metodo: e.target.value })}>{METODI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              <div style={{ flex: '1 1 140px' }}><label style={s.lab}>Data</label><input type="date" style={s.input} value={pag.data} onChange={e => setPag({ ...pag, data: e.target.value })} /></div>
            </div>
            <input style={{ ...s.input, marginTop: '8px' }} placeholder="Nota (facoltativa)" value={pag.note} onChange={e => setPag({ ...pag, note: e.target.value })} />
            <button style={{ ...s.primario, marginTop: '10px' }} onClick={() => azione(async () => { await api.post(`/tornei/partecipanti/${p.id}/pagamenti`, pag); setPag({ importo: '', metodo: pag.metodo, data: oggi(), note: '' }); })}>
              Registra {pag.importo ? eur(+pag.importo.replace(',', '.')) : eur(p.residuo)}
            </button>
            <div style={s.nota}>Importo vuoto = quanto manca.</div>
          </div>
        )}
      </div>

      {/* anagrafica */}
      <div style={s.box}>
        <div style={s.etichetta}>DATI</div>
        <fieldset disabled={!puoEdit} style={{ border: 'none', padding: 0, margin: 0 }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {[['cognome', 'Cognome'], ['nome', 'Nome'], ['email', 'Email'], ['telefono', 'Telefono'], ['codice_fiscale', 'Codice fiscale'], ['squadra', 'Squadra']].map(([k, l]) => (
              <div key={k} style={{ flex: '1 1 200px' }}><label style={s.lab}>{l}</label><input style={s.input} value={ana[k]} onChange={e => setAna({ ...ana, [k]: e.target.value })} /></div>
            ))}
            <div style={{ flex: '0 0 100px' }}><label style={s.lab}>Quote</label><input style={s.input} type="number" min="1" max="20" value={ana.quote} onChange={e => setAna({ ...ana, quote: e.target.value })} /></div>
          </div>
          <label style={s.lab}>Note</label>
          <textarea style={{ ...s.input, minHeight: '60px' }} value={ana.note} onChange={e => setAna({ ...ana, note: e.target.value })} />
        </fieldset>
        {(p.taglia_sopra || p.taglia_sotto) && <div style={s.nota}>Taglie: sopra {p.taglia_sopra || '—'} · sotto {p.taglia_sotto || '—'}</div>}
        {puoEdit && <button style={{ ...s.secondario, marginTop: '10px' }} onClick={() => azione(() => api.put(`/tornei/partecipanti/${p.id}`, ana))}>Salva dati</button>}
      </div>

      {err && <div style={s.err}>{err}</div>}
      {puoDel && <button style={{ ...s.miniNo, marginTop: '16px' }} onClick={() => window.confirm(`Togliere ${p.cognome} ${p.nome} da ${torneo.nome}?`) && azione(async () => { await api.delete(`/tornei/partecipanti/${p.id}`); onChiudi(); })}><Trash2 size={13} /> Togli dalla lista</button>}
    </Finestra>
  );
}

function NuovoPartecipante({ torneoId, onChiudi, onFatto }) {
  const [f, setF] = useState({ cognome: '', nome: '', email: '', telefono: '', codice_fiscale: '', squadra: '', quote: '1', tesserato: false });
  const [err, setErr] = useState('');
  const salva = async () => {
    setErr('');
    try { await api.post(`/tornei/${torneoId}/partecipanti`, f); onFatto(); onChiudi(); } catch (e) { setErr(errore(e, 'Salvataggio non riuscito')); }
  };
  return (
    <Finestra titolo="Aggiungi persona" onChiudi={onChiudi}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {[['cognome', 'Cognome *'], ['nome', 'Nome *'], ['email', 'Email'], ['telefono', 'Telefono'], ['codice_fiscale', 'Codice fiscale'], ['squadra', 'Squadra']].map(([k, l]) => (
          <div key={k} style={{ flex: '1 1 200px' }}><label style={s.lab}>{l}</label><input style={s.input} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} /></div>
        ))}
        <div style={{ flex: '0 0 100px' }}><label style={s.lab}>Quote</label><input style={s.input} type="number" min="1" max="20" value={f.quote} onChange={e => setF({ ...f, quote: e.target.value })} /></div>
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
        <span style={{ fontSize: '14px' }}>Tesserato a mano:</span>
        {[true, false].map(v => <button key={String(v)} style={{ ...s.pill, ...(f.tesserato === v ? s.pillOn : {}) }} onClick={() => setF({ ...f, tesserato: v })}>{v ? 'Sì' : 'No'}</button>)}<span style={s.nota}>se ha già ASI A o B in anagrafica (stesso CF) lo vede da solo</span>
      </div>
      {err && <div style={s.err}>{err}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={s.primario} onClick={salva}>Aggiungi</button>
        <button style={s.secondario} onClick={onChiudi}>Annulla</button>
      </div>
    </Finestra>
  );
}

// ── chiusura finale (irreversibile) ──────────────────────────────────────────────────────
function ChiudiTorneo({ dati, onChiudi, onFatto }) {
  const { torneo: t, riepilogo: r, partecipanti } = dati;
  const mancano = partecipanti.filter(p => p.stato === 'DA_PAGARE' || p.stato === 'PARZIALE');
  const contanti = r.per_metodo.CONTANTI || 0;
  const [affidati, setAffidati] = useState('');
  const [nota, setNota] = useState('');
  const [capito, setCapito] = useState(false);
  const [err, setErr] = useState('');
  const chiudi = async () => {
    setErr('');
    try { await api.post(`/tornei/${t.id}/chiudi`, { contanti_affidati_a: affidati, nota, conferma: true }); onFatto(); }
    catch (e) { setErr(errore(e, 'Chiusura non riuscita')); }
  };
  return (
    <Finestra titolo={`Chiudi ${t.nome}`} onChiudi={onChiudi}>
      {mancano.length > 0 ? (<>
        <div style={s.avvisoBox}><AlertTriangle size={16} style={{ flexShrink: 0 }} /><div>Il torneo si chiude solo quando hanno pagato tutti. Mancano ancora:</div></div>
        <div style={{ marginTop: '10px' }}>{mancano.map(p => <div key={p.id} style={{ fontSize: '14px', padding: '4px 0' }}>{p.cognome} {p.nome} · <b>{eur(p.residuo)}</b></div>)}</div>
        <button style={{ ...s.secondario, marginTop: '14px' }} onClick={onChiudi}>Ok</button>
      </>) : (<>
        <div style={s.box}>
          <div style={s.etichetta}>RIEPILOGO FINALE</div>
          <div style={{ fontSize: '15px' }}>{r.partecipanti} persone{r.staff ? ` (${r.staff} staff)` : ''} · dovuto <b>{eur(r.dovuto)}</b> · incassato <b>{eur(r.incassato)}</b></div>
          <div style={s.nota}>{METODI.map(([m, l]) => `${l} ${eur(r.per_metodo[m] || 0)}`).join(' · ')}</div>
        </div>
        {contanti > 0 && (<>
          <label style={s.lab}>A chi sono stati affidati i contanti ({eur(contanti)})? *</label>
          <input style={s.input} value={affidati} onChange={e => setAffidati(e.target.value)} placeholder="es. Mario Rossi, li verserà lunedì" />
        </>)}
        <label style={s.lab}>Nota</label>
        <textarea style={{ ...s.input, minHeight: '70px' }} value={nota} onChange={e => setNota(e.target.value)} placeholder="facoltativa" />
        <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginTop: '14px', fontSize: '14px' }}>
          <input type="checkbox" checked={capito} onChange={e => setCapito(e.target.checked)} style={{ marginTop: '3px' }} />
          <span>Ho capito che la chiusura è <b>definitiva</b>: dopo non si possono più cambiare persone, importi o pagamenti.</span>
        </label>
        {err && <div style={s.err}>{err}</div>}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button style={{ ...s.chiudiBtn, opacity: capito && (contanti <= 0 || affidati.trim().length > 1) ? 1 : 0.5 }} disabled={!capito || (contanti > 0 && affidati.trim().length < 2)} onClick={chiudi}><Lock size={15} /> Chiudi definitivamente</button>
          <button style={s.secondario} onClick={onChiudi}>Annulla</button>
        </div>
      </>)}
    </Finestra>
  );
}

// ── storico: chi ha fatto cosa ───────────────────────────────────────────────────────────
const descriviVoce = (v) => {
  const d = v.dettaglio || {};
  const chi = d.partecipante || d.aggiunto || d.eliminato || '';
  const sino = (x) => (x ? 'sì' : 'no');
  switch (v.azione) {
    case 'PAGAMENTO_TORNEO': if (d.corretto_da !== undefined) return `Pagamento ${NOME_METODO[d.metodo] || d.metodo} di ${chi} corretto da ${eur(d.corretto_da)} a ${eur(d.importo)}${d.motivo ? ` — ${d.motivo}` : ''}`;
      return `Pagamento ${eur(d.importo)} ${NOME_METODO[d.metodo] || d.metodo} di ${chi}`;
    case 'ELIMINA_PAGAMENTO_TORNEO': return `Eliminato pagamento ${eur(d.importo)} ${NOME_METODO[d.metodo] || d.metodo} di ${chi}`;
    case 'MODIFICA_IMPORTO_TORNEO': return d.dopo === null || d.dopo === undefined ? `${chi}: tornato al prezzo calcolato` : `${chi}: importo ${eur(d.dopo)} invece di ${eur(d.calcolato)} — ${d.motivo}`;
    case 'IMPORTA_PARTECIPANTI_TORNEO': return `Import Dink: ${d.nuovi} nuovi, ${d.aggiornati} aggiornati${d.errori ? `, ${d.errori} scartati` : ''}`;
    case 'CHIUDI_TORNEO': return `Torneo chiuso · incassato ${eur(d.incassato)}${d.contanti_affidati_a ? ` · contanti affidati a ${d.contanti_affidati_a}` : ''}${d.nota ? ` · ${d.nota}` : ''}`;
    default:
      if (d.acqua_consegnata) return `Acqua consegnata a ${chi}`;
      if (d.acqua_sbloccata) return `Acqua di ${chi} sbloccata: ${d.motivo}`;
      if (d.acqua_sblocco_negato) return `Sblocco acqua di ${chi} rifiutato: password errata`;
      if (d.acqua) return d.acqua.inclusa ? `Acqua inclusa${d.acqua.costo ? `, costo ${eur(d.acqua.costo)}` : ''}` : 'Acqua non inclusa';
      if (d.spesa) return `Spesa aggiunta: ${d.spesa} ${eur(d.importo)}`;
      if (d.spesa_modificata) return `Spesa modificata: ${d.spesa_modificata.prima} → ${d.spesa_modificata.dopo}`;
      if (d.spesa_eliminata) return `Spesa eliminata: ${d.spesa_eliminata} ${eur(d.importo)}`;
      if (d.aggiunto) return `Aggiunto ${d.aggiunto}${d.staff ? ' (staff)' : d.tesserato ? ' (tesserato)' : ''}`;
      if (d.eliminato) return `Tolto dalla lista ${d.eliminato}`;
      if (d.crea) return `Torneo creato (standard ${eur(d.standard)}, tesserato ${eur(d.tesserato)})`;
      if (d.prima && d.dopo) return `Torneo modificato: ${['nome', 'data', 'standard', 'tesserato'].filter(k => d.prima[k] !== d.dopo[k]).map(k => `${k} ${d.prima[k] ?? '—'} → ${d.dopo[k] ?? '—'}`).join(', ') || 'nessun cambio'}`;
      if (d.cambi) return `${chi}: ` + Object.entries(d.cambi).map(([k, [a, b]]) => ['tesserato', 'staff'].includes(k) ? `${k} ${sino(a)} → ${sino(b)}` : `${k} ${a ?? '—'} → ${b ?? '—'}`).join(', ');
      return v.azione;
  }
};
function Storico({ torneo, onChiudi }) {
  const [voci, setVoci] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.get(`/tornei/${torneo.id}/storico`).then(r => setVoci(r.data)).catch(e => setErr(errore(e, 'Impossibile caricare lo storico'))); }, [torneo.id]);
  return (
    <Finestra titolo={`Storico · ${torneo.nome}`} onChiudi={onChiudi} largo>
      <p style={{ ...s.sub, marginBottom: '8px' }}>Chi ha fatto cosa su questo torneo, dal più recente.</p>
      {err && <div style={s.err}>{err}</div>}
      {!voci ? <div style={s.vuoto}>Caricamento…</div> : voci.length === 0 ? <div style={s.vuoto}>Nessuna operazione registrata.</div> : voci.map(v => (
        <div key={v.id} style={s.rigaPag}>
          <span style={{ ...s.nota, marginTop: 0, minWidth: '120px' }}>{new Date(v.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
          <b style={{ minWidth: '110px' }}>{v.user_nome}</b>
          <span style={{ flex: 1 }}>{descriviVoce(v)}</span>
        </div>
      ))}
    </Finestra>
  );
}

// ── regole in testa al torneo (17/10, titolare: "così chi lo gestisce lo sa") + promemoria ASI A
function RegoleTorneo({ torneo: t, partecipanti, onApri, onEsporta }) {
  if (t.fonte_fipav) return (
    <div style={s.regole}>
      <div style={s.etichetta}>COME FUNZIONA QUESTO TORNEO</div>
      <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13.5px', lineHeight: 1.55 }}>
        <li><b>Prezzo unico {eur(t.prezzo_standard)}</b> a persona per ogni tabellone, corsisti e non. Le tessere qui non contano.</li>
        <li>Staff: non paga. Per gli altri casi puoi modificare l'importo dalla scheda della persona, con il motivo.</li>
        <li>Persone prese <b>una volta sola</b> il {gg(String(t.creato_il).slice(0, 10))} dalle liste FIPAV di: <b>{t.fonte_fipav.map(x => x.titolo).join(', ')}</b>. Se la lista cambia dopo, qui non cambia niente: aggiungi o togli le persone a mano.</li>
      </ul>
    </div>
  );
  const daTess = partecipanti.filter(p => p.da_tesserare);
  const daVerificare = partecipanti.filter(p => p.verifica_b?.length);
  return (
    <div style={s.regole}>
      <div style={s.etichetta}>COME FUNZIONA QUESTO TORNEO</div>
      <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13.5px', lineHeight: 1.55 }}>
        {t.sconto_tesserati ? <>
          <li><b>Prezzo tesserato {eur(t.prezzo_tesserato)}</b> a chi ha l'<b>ASI A</b> valida alla data del torneo, oppure è nel <b>planning corsi</b> o <b>iscritto AIBVC</b> di questa stagione (riconosciuti da codice fiscale o nome e cognome). Tutti gli altri <b>{eur(t.prezzo_standard)}</b>.</li>
          <li>L'<b>ASI B da sola non basta</b>: serve anche essere nel planning corsi o all'AIBVC. Chi è nel planning/AIBVC ma non ha la B nel gestionale ha lo sconto, con l'avviso "da verificare".</li>
        </> : <li><b>Torneo senza sconto tesserati</b> (es. federale): tutti pagano <b>{eur(t.prezzo_standard)}</b>.</li>}
        <li>Le tessere ASI con <b>scadenza 31/12/2026 non valgono</b> (errore di ASI, dovevano scadere il 31/08/2026).</li>
        <li>Chi non ha l'ASI A e non è nel planning né all'AIBVC <b>va tesserato ASI A</b> (la A è la tessera dei tornei; la FIPAV è un tesseramento a parte e qui non conta). Staff: non paga e non è nel promemoria.</li>
        <li>"Tesserato a mano" solo se la tessera esiste ma non è ancora nel gestionale.</li>
      </ul>
      {daTess.length > 0 ? (
        <div style={s.promemoria}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <b><AlertTriangle size={15} style={{ verticalAlign: '-2px' }} /> Da tesserare ASI A: {daTess.length} {daTess.length === 1 ? 'persona' : 'persone'}</b>
            <button style={s.secondario} onClick={onEsporta}><Download size={14} /> Elenco Excel</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
            {daTess.map(p => <button key={p.id} style={s.chipNome} onClick={() => onApri(p.id)} title={!p.codice_fiscale ? 'Codice fiscale mancante' : !p.persona_id ? 'Non ancora in anagrafica' : ''}>{p.cognome} {p.nome}{!p.codice_fiscale ? ' · CF?' : ''}</button>)}
          </div>
        </div>
      ) : partecipanti.length > 0 && <div style={{ ...s.nota, color: '#15803D', fontWeight: 600, marginTop: '8px' }}>Tutti gli iscritti hanno una tessera valida o sono corsisti.</div>}
      {daVerificare.length > 0 && (
        <div style={s.promemoria}>
          <b><AlertTriangle size={15} style={{ verticalAlign: '-2px' }} /> Tessera B da verificare: {daVerificare.length} {daVerificare.length === 1 ? 'persona' : 'persone'}</b>
          <div style={{ fontSize: '13px', marginTop: '4px' }}>Nel planning corsi o all'AIBVC ma senza ASI B valida nel gestionale: lo sconto c'è, controlla il perché.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
            {daVerificare.map(p => <div key={p.id} style={{ fontSize: '13px' }}><button style={s.chipNome} onClick={() => onApri(p.id)}>{p.cognome} {p.nome}</button> {p.verifica_b.join('; ')}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

// ── spese del torneo (17/10, titolare): righe libere (costo campo, staff, premi…) solo per statistica.
// Si scrivono anche a torneo chiuso: la chiusura riguarda gli incassi.
function SpeseTorneo({ torneo, spese, riepilogo: r, puoScrivere, onCambio }) {
  const vuota = { voce: '', importo: '', note: '' };
  const [f, setF] = useState(null); // null = chiuso; { id? , voce, importo, note }
  const [voci, setVoci] = useState([]);
  const [err, setErr] = useState('');
  useEffect(() => { if (f && !voci.length) api.get('/tornei/voci-spesa').then(x => setVoci(x.data)).catch(() => {}); }, [f]); // eslint-disable-line
  const salva = async () => {
    try {
      if (f.id) await api.put(`/tornei/spese/${f.id}`, f); else await api.post(`/tornei/${torneo.id}/spese`, f);
      setF(null); setErr(''); onCambio();
    } catch (e) { setErr(errore(e, 'Spesa non salvata')); }
  };
  const elimina = async (x) => {
    if (!window.confirm(`Eliminare la spesa "${x.voce}" di ${eur(x.importo)}?`)) return;
    try { await api.delete(`/tornei/spese/${x.id}`); onCambio(); } catch (e) { alert(errore(e, 'Eliminazione non riuscita')); }
  };
  return (
    <div style={{ ...s.card, cursor: 'default', marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <div>
          <div style={s.etichetta}>SPESE DEL TORNEO</div>
          <div style={{ fontSize: '14px', marginTop: '4px' }}>
            Incassato {eur(r.incassato)} − spese {eur(r.spese)} = <b style={{ color: r.risultato < 0 ? '#B42318' : '#15803D' }}>{eur(r.risultato)}</b>
          </div>
        </div>
        {puoScrivere && !f && <button style={s.secondario} onClick={() => setF(vuota)}><Plus size={15} /> Aggiungi spesa</button>}
      </div>
      {spese.length === 0 && !f && <div style={s.nota}>Nessuna spesa inserita: costo campo, staff, premi… servono per vedere l'andamento in dashboard.</div>}
      {spese.map(x => (
        <div key={x.id} style={s.rigaPag}>
          <b style={{ flex: 1 }}>{x.voce}{x.note && <span style={{ ...s.nota, fontWeight: 400 }}> · {x.note}</span>}</b>
          <span style={{ fontWeight: 700 }}>{eur(x.importo)}</span>
          {puoScrivere && <button style={{ ...s.miniNo, background: 'var(--bg-input)', color: 'var(--text)' }} title="Modifica" onClick={() => setF({ id: x.id, voce: x.voce, importo: String(x.importo), note: x.note || '' })}><Pencil size={13} /></button>}
          {puoScrivere && <button style={s.miniNo} title="Elimina" onClick={() => elimina(x)}><Trash2 size={13} /></button>}
        </div>
      ))}
      {f && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '10px' }}>
          <input style={{ ...s.input, flex: '2 1 180px', width: 'auto' }} list="voci-spesa-torneo" placeholder="Voce (es. Costo campo)" value={f.voce} onChange={e => setF({ ...f, voce: e.target.value })} autoFocus />
          <datalist id="voci-spesa-torneo">{voci.map(v => <option key={v} value={v} />)}</datalist>
          <input style={{ ...s.input, flex: '1 1 90px', width: 'auto' }} inputMode="decimal" placeholder="€" value={f.importo} onChange={e => setF({ ...f, importo: e.target.value })} />
          <input style={{ ...s.input, flex: '2 1 160px', width: 'auto' }} placeholder="Nota (facoltativa)" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} onKeyDown={e => e.key === 'Enter' && salva()} />
          <button style={s.primario} onClick={salva}>Salva</button>
          <button style={s.secondario} onClick={() => { setF(null); setErr(''); }}>Annulla</button>
        </div>
      )}
      {err && <div style={s.err}>{err}</div>}
    </div>
  );
}

// ── dettaglio torneo ─────────────────────────────────────────────────────────────────────
function DettaglioTorneo({ id, onIndietro, onCambio }) {
  const { user } = useAuth();
  const mobile = useIsMobile();
  const [dati, setDati] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [scelti, setScelti] = useState({});
  const [aperto, setAperto] = useState(null);
  const [finestra, setFinestra] = useState(null); // 'modifica' | 'importa' | 'nuovo' | 'chiudi' | 'storico'
  const chiuso = !!dati?.torneo.chiuso_at;
  const puo = (a) => !chiuso && puoFare(user, 'tornei', a);
  const carica = () => api.get(`/tornei/${id}`).then(r => { setDati(r.data); setErr(''); }).catch(e => setErr(errore(e, 'Torneo non trovato')));
  useEffect(() => { carica(); }, [id]); // eslint-disable-line
  const aggiorna = () => { carica(); onCambio(); };

  const filtri = useMemo(() => {
    const l = dati?.partecipanti || [];
    const metodi = (p) => new Set(p.pagamenti.map(x => x.metodo));
    return [
      { k: 'stato', label: 'Stato', opzioni: [
        ...Object.entries(STATO).map(([k, v]) => [k, v.label, p => p.stato === k]),
      ] },
      { k: 'metodo', label: 'Pagato con', opzioni: [
        ...METODI.map(([m, l]) => [m, l, p => metodi(p).has(m)]),
        ['misto', 'Più metodi', p => metodi(p).size > 1],
        ['nessuno', 'Nessun pagamento', p => p.pagamenti.length === 0],
      ] },
      ...(dati?.torneo?.fonte_fipav ? [] : [{ k: 'tessera', label: 'Tesserato', opzioni: [
        ['ASI B', 'Con ASI B', p => (p.tessera_asi || '').startsWith('ASI B')],
        ['ASI A', 'Con ASI A', p => (p.tessera_asi || '').startsWith('ASI A')],
        ['corso', 'Corsisti (dal planning)', p => p.tesserato_fonte === 'CORSO'],
        ['aibvc', 'Iscritti AIBVC', p => p.tesserato_fonte === 'AIBVC'],
        ['verifica_b', 'Tessera B da verificare', p => p.verifica_b?.length > 0],
        ['b_senza_corso', 'ASI B senza corso/AIBVC', p => p.b_senza_corso],
        ['mano', 'Tesserato a mano', p => p.tesserato_fonte === 'MANUALE'],
        ['no', 'Non tesserati', p => !p.tesserato],
        ['errata', 'Tessera ASI 31/12 non valida', p => p.tessera_errata],
        ['da_tesserare', 'Da tesserare ASI A', p => p.da_tesserare],
      ] }]),
      ...(dati?.torneo?.acqua_inclusa ? [{ k: 'acqua', label: 'Acqua', opzioni: [['si', 'Consegnata', p => p.acqua_consegnata], ['no', 'Da consegnare', p => !p.acqua_consegnata]] }] : []),
      { k: 'data', label: 'Data pagamento', opzioni: distinti(l.flatMap(p => p.pagamenti.map(x => x.data))).map(d => [d, gg(d), p => p.pagamenti.some(x => x.data === d)]) },
      { k: 'registrato', label: 'Registrato da', opzioni: distinti(l.flatMap(p => p.pagamenti.map(x => x.registrato_da))).map(n => [n, n, p => p.pagamenti.some(x => x.registrato_da === n)]) },
      { k: 'importo', label: 'Importo', opzioni: [
        ['modificato', 'Importo modificato', p => p.importo_modificato !== null],
        ['quote', 'Più quote (più tabelloni)', p => p.quote > 1],
        ['eccedenza', 'Ha pagato troppo', p => p.eccedenza > 0],
      ] },
      { k: 'tabellone', label: 'Tabellone', opzioni: distinti(l.map(p => p.tabelloni)).map(v => [v, v, p => p.tabelloni === v]) },
      { k: 'genere', label: 'Genere', opzioni: distinti(l.map(p => p.genere)).map(v => [v, v, p => p.genere === v]) },
      { k: 'anagrafica', label: 'Anagrafica', opzioni: [
        ['cf_mancante', 'Senza codice fiscale', p => !p.codice_fiscale],
        ['fuori', 'Non in anagrafica', p => !!p.codice_fiscale && !p.persona_id],
        ['dentro', 'In anagrafica', p => !!p.persona_id],
      ] },
      { k: 'origine', label: 'Inserito', opzioni: [['IMPORT', 'Da Dink', p => p.origine === 'IMPORT'], ['FIPAV', 'Dalla lista FIPAV', p => p.origine === 'FIPAV'], ['MANUALE', 'A mano', p => p.origine === 'MANUALE']] },
    ];
  }, [dati]);
  const righe = useMemo(() => {
    const t = q.trim().toLowerCase();
    return applicaFiltri(dati?.partecipanti || [], filtri, scelti)
      .filter(p => !t || `${p.cognome} ${p.nome} ${p.squadra || ''} ${p.email || ''} ${p.codice_fiscale || ''} ${p.telefono || ''}`.toLowerCase().includes(t));
  }, [dati, q, filtri, scelti]);
  const filtrato = !!q.trim() || Object.values(scelti).some(Boolean);
  // totali delle sole righe filtrate; con un metodo scelto conta solo quel metodo (es. "quanti contanti")
  const totFiltro = useMemo(() => {
    const m = scelti.metodo || '';
    const pagamenti = righe.flatMap(p => p.pagamenti).filter(x => (!METODI.some(([k]) => k === m) || x.metodo === m) && (!scelti.data || x.data === scelti.data) && (!scelti.registrato || x.registrato_da === scelti.registrato));
    const per = Object.fromEntries(METODI.map(([k]) => [k, pagamenti.filter(x => x.metodo === k).reduce((a, x) => a + x.importo, 0)]));
    return { dovuto: righe.reduce((a, p) => a + p.dovuto, 0), residuo: righe.reduce((a, p) => a + p.residuo, 0), incassato: pagamenti.reduce((a, x) => a + x.importo, 0), per };
  }, [righe, scelti]);

  const pagaVeloce = async (p, metodo) => {
    try {
      if (metodo === 'STAFF') await api.put(`/tornei/partecipanti/${p.id}`, { staff: true });
      else await api.post(`/tornei/partecipanti/${p.id}/pagamenti`, { metodo });
      aggiorna();
    } catch (e) { alert(errore(e, 'Pagamento non registrato')); }
  };
  const elimina = async () => {
    if (!window.confirm(`Eliminare il torneo "${dati.torneo.nome}" e la sua lista?`)) return;
    try { await api.delete(`/tornei/${id}`); onCambio(); onIndietro(); } catch (e) { alert(errore(e, 'Eliminazione non riuscita')); }
  };
  const esporta = () => {
    const cella = (v) => { const x = v === null || v === undefined ? '' : String(v); return /[;"\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x; };
    const num = (n) => String(n).replace('.', ',');
    const intest = ['Cognome', 'Nome', 'Email', 'Telefono', 'Codice fiscale', 'Squadra', 'Tabelloni', 'Tesserato', 'Tessera ASI', 'Staff', 'Quote', 'Dovuto', 'Motivo modifica', 'Pagato', 'Metodi', 'Stato', ...(dati.torneo.acqua_inclusa ? ['Acqua consegnata'] : [])];
    const corpo = righe.map(p => [p.cognome, p.nome, p.email, p.telefono, p.codice_fiscale, p.squadra, p.tabelloni, p.tesserato ? 'Sì' : 'No', p.tessera_asi || (p.tesserato ? 'a mano' : ''), p.staff ? 'Sì' : 'No', p.quote, num(p.dovuto), p.motivo_modifica, num(p.pagato),
      p.pagamenti.map(x => `${NOME_METODO[x.metodo]} ${num(x.importo)}`).join(' + '), STATO[p.stato].label, ...(dati.torneo.acqua_inclusa ? [p.acqua_consegnata ? 'Sì' : 'No'] : [])]);
    const csv = '﻿' + [intest, ...corpo].map(r => r.map(cella).join(';')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `${dati.torneo.nome.replace(/[^\w\- ]+/g, '').trim() || 'torneo'}${filtrato ? ' - filtrati' : ''}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };
  // da tesserare ASI A (17/10, titolare): niente ASI B/A valida, non corsista, non staff (FIPAV non conta)
  const esportaNonTesserati = () => {
    const cella = (v) => { const x = v === null || v === undefined ? '' : String(v); return /[;"\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x; };
    const intest = ['Cognome', 'Nome', 'Codice fiscale', 'Email', 'Telefono', 'Squadra', 'Nota'];
    const corpo = dati.partecipanti.filter(p => p.da_tesserare).map(p => [p.cognome, p.nome, p.codice_fiscale, p.email, p.telefono, p.squadra,
      !p.codice_fiscale ? 'CF mancante' : !p.persona_id ? 'non ancora in anagrafica' : p.b_senza_corso ? 'ha la ASI B ma non è nel planning né AIBVC' : p.tessera_errata ? 'ha solo la tessera ASI 31/12/2026' : '']);
    const csv = '\ufeff' + [intest, ...corpo].map(r => r.map(cella).join(';')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `da tesserare ASI A - ${dati.torneo.nome.replace(/[^\w\- ]+/g, '').trim() || 'torneo'}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  if (err) return <div><button style={s.secondario} onClick={onIndietro}><ArrowLeft size={15} /> Tutti i tornei</button><div style={s.err}>{err}</div></div>;
  if (!dati) return <div style={s.vuoto}>Caricamento…</div>;
  const { torneo: t, riepilogo: r } = dati;
  const pAperto = aperto && dati.partecipanti.find(p => p.id === aperto);

  return (
    <div>
      <button style={s.secondario} onClick={onIndietro}><ArrowLeft size={15} /> Tutti i tornei</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start', marginTop: '14px' }}>
        <div>
          <span style={{ ...s.badge, background: '#FFF1E8', color: '#C24B08' }}>{t.tipologia}</span>
          <h2 style={{ ...s.h2, margin: '8px 0 4px' }}>{t.nome}</h2>
          <div style={s.info}>{t.data && <><Calendar size={14} /> {gg(t.data)}</>}{t.luogo && <><MapPin size={14} style={{ marginLeft: t.data ? '8px' : 0 }} /> {t.luogo}</>}</div>
          <div style={s.nota}>{t.sconto_tesserati ? <>Standard {eur(t.prezzo_standard)} · Tesserato {eur(t.prezzo_tesserato)} a persona</> : <>Prezzo unico {eur(t.prezzo_standard)} a persona · <span style={s.senzaSconto}>senza sconto tesserati</span></>}</div>
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {puo('create') && !t.fonte_fipav && <button style={s.primario} onClick={() => setFinestra('importa')}><Upload size={15} /> Importa da Dink</button>}
          {puo('create') && <button style={s.secondario} onClick={() => setFinestra('nuovo')}><Plus size={15} /> Aggiungi persona</button>}
          {dati.partecipanti.length > 0 && <button style={s.secondario} onClick={esporta}><Download size={15} /> {filtrato ? `Excel (${righe.length} filtrati)` : 'Excel'}</button>}
          {r.da_tesserare > 0 && <button style={s.secondario} onClick={esportaNonTesserati}><Download size={15} /> Da tesserare ASI A ({r.da_tesserare})</button>}
          <button style={s.secondario} onClick={() => setFinestra('storico')}><History size={15} /> Storico</button>
          {puo('edit') && <button style={s.secondario} onClick={() => setFinestra('modifica')}><Pencil size={15} /> Modifica</button>}
          {puo('edit') && dati.partecipanti.length > 0 && <button style={s.chiudiBtn} onClick={() => setFinestra('chiudi')}><Lock size={15} /> Chiudi torneo</button>}
          {puo('delete') && <button style={s.miniNo} onClick={elimina}><Trash2 size={14} /></button>}
        </div>
      </div>

      {chiuso && (
        <div style={s.chiusoBox}>
          <Lock size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <b>Torneo chiuso</b> il {new Date(t.chiuso_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} da {t.chiuso_da}. Non si può più modificare.
            {t.contanti_affidati_a && <div>Contanti ({eur(t.riepilogo_chiusura?.per_metodo?.CONTANTI)}) affidati a <b>{t.contanti_affidati_a}</b></div>}
            {t.nota_chiusura && <div>Nota: <i>{t.nota_chiusura}</i></div>}
          </div>
        </div>
      )}
      <DialogoHost />
      <RegoleTorneo torneo={t} partecipanti={dati.partecipanti} onApri={setAperto} onEsporta={esportaNonTesserati} />
      <div style={s.kpi}>
        {[['Dovuto', r.dovuto], ['Incassato', r.incassato], ['Da incassare', r.da_incassare]].map(([l, v]) => (
          <div key={l} style={s.kpiBox}><div style={s.etichetta}>{l.toUpperCase()}</div><div style={{ fontSize: '22px', fontWeight: 800, color: l === 'Da incassare' && v > 0 ? '#B42318' : 'var(--text)' }}>{eur(v)}</div></div>
        ))}
        <div style={s.kpiBox}><div style={s.etichetta}>PERSONE</div><div style={{ fontSize: '22px', fontWeight: 800 }}>{r.partecipanti}</div><div style={s.nota}>{r.da_pagare} devono ancora pagare{r.staff ? ` · ${r.staff} staff` : ''}{r.non_tesserati && !t.fonte_fipav ? ` · ${r.non_tesserati} non tesserati` : ''}</div></div>
      </div>
      {t.acqua_inclusa && (
        <div style={{ ...s.regole, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '14px' }}>
          <Droplets size={16} color="#1D4ED8" /> <b>Acqua inclusa</b> · consegnate {r.acqua_consegnate} su {r.partecipanti}
          {t.acqua_costo ? <> · costo {eur(t.acqua_costo)} (nelle spese)</> : <span style={{ color: '#B45309', fontWeight: 600 }}> · costo non ancora scritto: Modifica</span>}
        </div>
      )}
      <SpeseTorneo torneo={t} spese={dati.spese} riepilogo={r} puoScrivere={puoFare(user, 'tornei', 'edit')} onCambio={aggiorna} />
      {r.incassato > 0 && <div style={{ ...s.nota, marginBottom: '14px' }}>Incassato: {METODI.filter(([m]) => r.per_metodo[m]).map(([m, l]) => `${l} ${eur(r.per_metodo[m])}`).join(' · ')}</div>}

      <div style={s.filtri}>
        <div style={s.cerca}><Search size={16} color="var(--text-dim)" /><input style={s.cercaInput} placeholder="Cerca nome, squadra, email…" value={q} onChange={e => setQ(e.target.value)} /></div>
        <Filtri filtri={filtri} scelti={scelti} setScelti={setScelti} righe={dati.partecipanti} />
        {filtrato && dati.partecipanti.length > 0 && (
          <div style={s.totFiltro}>
            <b>{righe.length} di {dati.partecipanti.length} persone</b>
            <span>Dovuto {eur(totFiltro.dovuto)}</span>
            <span>Incassato {eur(totFiltro.incassato)}{METODI.filter(([k]) => totFiltro.per[k]).length > 1 && ` (${METODI.filter(([k]) => totFiltro.per[k]).map(([k, l]) => `${l} ${eur(totFiltro.per[k])}`).join(' · ')})`}</span>
            {totFiltro.residuo > 0 && <span style={{ color: '#B42318', fontWeight: 600 }}>Da incassare {eur(totFiltro.residuo)}</span>}
          </div>
        )}
      </div>

      {dati.partecipanti.length === 0 ? (
        <div style={s.vuoto}><Trophy size={30} /><div style={{ marginTop: '8px' }}>Nessun iscritto. {t.fonte_fipav ? 'Aggiungi' : 'Importa il CSV da Dink o aggiungi'} le persone a mano.</div></div>
      ) : righe.length === 0 ? <div style={s.vuoto}>Nessuno con questi filtri.</div> : mobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {righe.map(p => (
            <div key={p.id} style={{ ...s.card }} onClick={() => setAperto(p.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}><b>{p.cognome} {p.nome}</b><Badge stato={p.stato} /></div>
              {p.squadra && <div style={s.nota}>{p.squadra}</div>}
              <div style={{ fontSize: '14px', marginTop: '6px' }}>{eur(p.dovuto)}{p.importo_modificato !== null && <span style={s.modif}> modificato</span>}{p.quote > 1 && ` (${p.quote} quote)`} · pagato {eur(p.pagato)} · {p.tessera_asi || (p.tesserato ? 'tesserato a mano' : <span style={{ color: '#B42318', fontWeight: 600 }}>non tesserato{p.tessera_errata ? ' (tessera 31/12 non vale)' : ''}</span>)}{p.da_tesserare && <span style={s.daTess}> da tesserare ASI A</span>}</div>
              {t.acqua_inclusa && <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }} onClick={e => e.stopPropagation()}>Acqua: <BottoneAcqua p={p} puoEdit={puo('edit')} puoSblocca={puo('delete')} chiuso={!!t.chiuso_at} onCambio={aggiorna} /></div>}
              {puo('edit') && p.residuo > 0 && (
                <select style={{ ...s.input, marginTop: '8px' }} value="" onClick={e => e.stopPropagation()} onChange={e => e.target.value && pagaVeloce(p, e.target.value)}>
                  <option value="">Segna pagato {eur(p.residuo)}…</option>
                  {METODI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  <option value="STAFF">È dello staff (omaggio)</option>
                </select>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.tabella}>
            <thead><tr>{['Persona', t.fonte_fipav ? 'Tabellone' : 'Tesserato', 'Dovuto', 'Pagato', 'Stato', ...(t.acqua_inclusa ? ['Acqua'] : []), ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {righe.map(p => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setAperto(p.id)}>
                  <td style={s.td}><b>{p.cognome} {p.nome}</b>{p.squadra && <div style={s.nota}>{p.squadra}</div>}</td>
                  <td style={s.td}>{t.fonte_fipav ? p.tabelloni : p.tessera_asi ? <span style={{ color: '#1D4ED8', fontWeight: 600 }}>{p.tessera_asi}</span> : p.tesserato ? 'Sì (a mano)' : <span style={{ color: '#B42318', fontWeight: 600 }}>No{p.tessera_errata && <div style={{ ...s.nota, fontWeight: 400 }}>tessera 31/12 non vale</div>}</span>}{p.da_tesserare && <div style={s.daTess}>da tesserare ASI A</div>}</td>
                  <td style={s.td}>{eur(p.dovuto)}{p.quote > 1 && <span style={s.nota}> ({p.quote} quote)</span>}{p.importo_modificato !== null && <div style={s.modif} title={p.motivo_modifica}>modificato: {p.motivo_modifica}</div>}</td>
                  <td style={s.td}>{eur(p.pagato)}{p.pagamenti.length > 0 && <div style={s.nota}>{[...new Set(p.pagamenti.map(x => NOME_METODO[x.metodo]))].join(', ')}</div>}</td>
                  <td style={s.td}><Badge stato={p.stato} /></td>
                  {t.acqua_inclusa && <td style={{ ...s.td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}><BottoneAcqua p={p} puoEdit={puo('edit')} puoSblocca={puo('delete')} chiuso={!!t.chiuso_at} onCambio={aggiorna} /></td>}
                  <td style={{ ...s.td, width: '1%' }} onClick={e => e.stopPropagation()}>
                    {puo('edit') && p.residuo > 0 && (
                      <select style={{ ...s.input, padding: '6px 10px', fontSize: '13px', width: 'auto' }} value="" onChange={e => e.target.value && pagaVeloce(p, e.target.value)}>
                        <option value="">Segna pagato {eur(p.residuo)}…</option>
                        {METODI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        <option value="STAFF">È dello staff (omaggio)</option>
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pAperto && <SchedaPartecipante key={pAperto.id + '-' + pAperto.pagamenti.length + '-' + pAperto.dovuto} p={pAperto} torneo={t} onChiudi={() => setAperto(null)} onCambio={aggiorna} />}
      {finestra === 'modifica' && <FormTorneo torneo={t} onChiudi={() => setFinestra(null)} onSalvato={() => { setFinestra(null); aggiorna(); }} />}
      {finestra === 'importa' && <ImportaDink torneoId={t.id} onChiudi={() => setFinestra(null)} onFatto={aggiorna} />}
      {finestra === 'nuovo' && <NuovoPartecipante torneoId={t.id} onChiudi={() => setFinestra(null)} onFatto={aggiorna} />}
      {finestra === 'chiudi' && <ChiudiTorneo dati={dati} onChiudi={() => setFinestra(null)} onFatto={() => { setFinestra(null); aggiorna(); }} />}
      {finestra === 'storico' && <Storico torneo={t} onChiudi={() => setFinestra(null)} />}
    </div>
  );
}

// ── elenco tornei ────────────────────────────────────────────────────────────────────────
function ElencoTornei({ versione, onApri }) {
  const { user } = useAuth();
  const [lista, setLista] = useState(null);
  const [err, setErr] = useState('');
  const [nuovo, setNuovo] = useState(false);
  const [daFipav, setDaFipav] = useState(false);
  const [q, setQ] = useState('');
  const [scelti, setScelti] = useState({});
  useEffect(() => { api.get('/tornei').then(r => setLista(r.data)).catch(e => setErr(errore(e, 'Impossibile caricare i tornei'))); }, [versione]);
  const filtri = useMemo(() => {
    const l = lista || [];
    return [
      { k: 'tipologia', label: 'Tipologia', opzioni: distinti(l.map(t => t.tipologia)).map(v => [v, v, t => t.tipologia === v]) },
      { k: 'stato', label: 'Stato', opzioni: [
        ['aperti', 'Aperti', t => !t.chiuso_at],
        ['chiusi', 'Chiusi', t => !!t.chiuso_at],
        ['incassare', 'Con soldi da incassare', t => t.riepilogo.da_incassare > 0],
        ['da_chiudere', 'Tutto pagato, da chiudere', t => !t.chiuso_at && t.riepilogo.partecipanti > 0 && t.riepilogo.da_pagare === 0],
        ['vuoti', 'Senza iscritti', t => t.riepilogo.partecipanti === 0],
        ['non_tesserati', 'Con non tesserati', t => t.riepilogo.non_tesserati > 0],
        ['da_tesserare', 'Con persone da tesserare ASI A', t => t.riepilogo.da_tesserare > 0],
      ] },
      { k: 'metodo', label: 'Incassato con', opzioni: METODI.map(([m, l]) => [m, l, t => t.riepilogo.per_metodo[m] > 0]) },
      { k: 'stagione', label: 'Stagione', opzioni: distinti(l.map(t => stagioneDi(t.data))).reverse().map(v => [v, v, t => stagioneDi(t.data) === v]) },
      { k: 'mese', label: 'Mese', opzioni: distinti(l.map(t => t.data && t.data.slice(0, 7))).reverse().map(v => [v, `${MESI[+v.slice(5) - 1]} ${v.slice(0, 4)}`, t => (t.data || '').startsWith(v)]) },
      { k: 'periodo', label: 'Quando', opzioni: [['futuri', 'Prossimi', t => !!t.data && t.data >= oggi()], ['passati', 'Già giocati', t => !!t.data && t.data < oggi()], ['senza_data', 'Senza data', t => !t.data]] },
      { k: 'luogo', label: 'Luogo', opzioni: distinti(l.map(t => t.luogo)).map(v => [v, v, t => t.luogo === v]) },
    ];
  }, [lista]);
  const visti = applicaFiltri(lista || [], filtri, scelti).filter(t => !q.trim() || `${t.nome} ${t.tipologia} ${t.luogo || ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  const filtrato = !!q.trim() || Object.values(scelti).some(Boolean);
  const somma = (k) => visti.reduce((a, t) => a + t.riepilogo[k], 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
        {puoFare(user, 'tornei', 'create') && <button style={s.primario} onClick={() => setNuovo(true)}><Plus size={16} /> Nuovo torneo</button>}
        {puoFare(user, 'tornei', 'create') && <button style={s.secondario} onClick={() => setDaFipav(true)}><Trophy size={16} /> Da torneo FIPAV</button>}
        {lista?.length > 0 && <div style={{ ...s.cerca, flex: '1 1 240px' }}><Search size={16} color="var(--text-dim)" /><input style={s.cercaInput} placeholder="Cerca torneo…" value={q} onChange={e => setQ(e.target.value)} /></div>}
      </div>
      {err && <div style={s.err}>{err}</div>}
      {lista?.length > 1 && <div style={{ marginBottom: '14px' }}><Filtri filtri={filtri} scelti={scelti} setScelti={setScelti} righe={lista} /></div>}
      {filtrato && lista?.length > 0 && (
        <div style={{ ...s.totFiltro, marginBottom: '14px' }}>
          <b>{visti.length} di {lista.length} tornei</b>
          <span>Dovuto {eur(somma('dovuto'))}</span>
          <span>Incassato {eur(somma('incassato'))}{` (${METODI.map(([m, l]) => `${l} ${eur(visti.reduce((a, t) => a + (t.riepilogo.per_metodo[m] || 0), 0))}`).join(' · ')})`}</span>
          {somma('da_incassare') > 0 && <span style={{ color: '#B42318', fontWeight: 600 }}>Da incassare {eur(somma('da_incassare'))}</span>}
          <span>{somma('partecipanti')} iscrizioni</span>
        </div>
      )}
      {/* resoconto (17/10, titolare): tornei fatti, incassi, spese, netto — segue i filtri (es. una stagione) */}
      {visti.length > 0 && (() => {
        const fatti = visti.filter(t => t.chiuso_at || (t.data && t.data <= oggi())).length, netto = somma('incassato') - somma('spese');
        return (
          <div style={s.kpi}>
            <div style={s.kpiBox}><div style={s.etichetta}>TORNEI FATTI</div><div style={{ fontSize: '22px', fontWeight: 800 }}>{fatti}</div>{visti.length > fatti && <div style={s.nota}>{visti.length - fatti} in programma</div>}</div>
            <div style={s.kpiBox}><div style={s.etichetta}>INCASSI</div><div style={{ fontSize: '22px', fontWeight: 800 }}>{eur(somma('incassato'))}</div>{somma('da_incassare') > 0 && <div style={{ ...s.nota, color: '#B42318' }}>+{eur(somma('da_incassare'))} da incassare</div>}</div>
            <div style={s.kpiBox}><div style={s.etichetta}>SPESE</div><div style={{ fontSize: '22px', fontWeight: 800 }}>{eur(somma('spese'))}</div>{visti.some(t => !t.spese?.length) && <div style={s.nota}>{visti.filter(t => !t.spese?.length).length} senza spese inserite</div>}</div>
            <div style={s.kpiBox}><div style={s.etichetta}>NETTO</div><div style={{ fontSize: '22px', fontWeight: 800, color: netto < 0 ? '#B42318' : '#15803D' }}>{eur(netto)}</div><div style={s.nota}>incassi − spese{filtrato ? ' · solo i tornei filtrati' : ''}</div></div>
          </div>
        );
      })()}
      {lista?.length > 0 && visti.length === 0 && <div style={s.vuoto}>Nessun torneo con questi filtri.</div>}
      {!lista ? <div style={s.vuoto}>Caricamento…</div> : lista.length === 0 ? (
        <div style={s.vuoto}><Trophy size={30} /><div style={{ marginTop: '8px' }}>Nessun torneo ancora. Crea prima una tipologia (scheda "Tipologie"), poi il torneo.</div></div>
      ) : (
        <div style={s.griglia}>
          {visti.map(t => {
            const r = t.riepilogo, perc = r.dovuto > 0 ? Math.min(100, Math.round(r.incassato / r.dovuto * 100)) : 0;
            return (
              <button key={t.id} style={s.card} onClick={() => onApri(t.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                  <span style={{ ...s.badge, background: '#FFF1E8', color: '#C24B08' }}>{t.tipologia}</span>
                  <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {t.chiuso_at && <span style={{ ...s.badge, background: '#DCFCE7', color: '#15803D' }}><Lock size={11} /> Chiuso</span>}
                    {t.data && <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{gg(t.data)}</span>}
                  </span>
                </div>
                <div style={{ fontWeight: 700, fontSize: '16px', marginTop: '8px' }}>{t.nome}</div>
                {t.luogo && <div style={s.luogo}><MapPin size={13} /> {t.luogo}</div>}
                <div style={s.barra}><div style={{ ...s.barraPiena, width: `${perc}%` }} /></div>
                <div style={{ fontSize: '13.5px' }}>{eur(r.incassato)} su {eur(r.dovuto)} · {r.partecipanti} persone</div>
                {r.spese > 0 && <div style={s.nota}>Spese {eur(r.spese)} · risultato <b style={{ color: r.risultato < 0 ? '#B42318' : '#15803D' }}>{eur(r.risultato)}</b></div>}
                {r.da_pagare > 0 && <div style={{ fontSize: '12.5px', color: '#B42318', fontWeight: 600, marginTop: '2px' }}>{r.da_pagare} da pagare · {eur(r.da_incassare)}</div>}
              </button>
            );
          })}
        </div>
      )}
      {nuovo && <FormTorneo onChiudi={() => setNuovo(false)} onSalvato={(id) => { setNuovo(false); onApri(id); }} />}
      {daFipav && <DaTorneoFipav onChiudi={() => setDaFipav(false)} onCreato={(id) => { setDaFipav(false); onApri(id); }} />}
    </div>
  );
}

export default function ContabilitaTornei() {
  const [params, setParams] = useSearchParams();
  const [versione, setVersione] = useState(0);
  const scheda = params.get('scheda') === 'tipologie' ? 'tipologie' : 'tornei';
  const torneo = +params.get('torneo') || null;
  const vai = (p) => setParams(p);
  const aggiorna = () => setVersione(v => v + 1);

  return (
    <div>
      <h1 style={s.titolo}>CONTABILITÀ TORNEI</h1>
      <p style={s.sub}>Chi partecipa, quanto deve e come ha pagato. Serve a tenere i conti dei tornei: non registra niente nei movimenti bancari.</p>
      {torneo ? <DettaglioTorneo id={torneo} onIndietro={() => vai({})} onCambio={aggiorna} /> : (<>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
          {[['tornei', 'Tornei'], ['tipologie', 'Tipologie']].map(([k, l]) => <button key={k} style={{ ...s.pill, ...(scheda === k ? s.pillOn : {}) }} onClick={() => vai(k === 'tornei' ? {} : { scheda: k })}>{l}</button>)}
        </div>
        {scheda === 'tornei' ? <ElencoTornei versione={versione} onApri={(id) => vai({ torneo: id })} /> : <Tipologie onCambio={aggiorna} />}
      </>)}
    </div>
  );
}

const s = {
  titolo: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight: 800, letterSpacing: '2px', marginBottom: '6px' },
  sub: { color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: '16px', maxWidth: '820px' },
  filtri: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' },
  cerca: { display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '12px', padding: '0 12px', maxWidth: '520px' },
  cercaInput: { flex: 1, border: 'none', background: 'transparent', padding: '11px 0', outline: 'none', color: 'var(--text)', fontSize: '15px', boxShadow: 'none' },
  pill: { border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text-muted)', borderRadius: '999px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  filtriGriglia: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '8px' },
  labFiltro: { display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '3px' },
  selectOn: { borderColor: '#F4600C', boxShadow: '0 0 0 1px #F4600C' },
  totFiltro: { display: 'flex', gap: '6px 16px', flexWrap: 'wrap', alignItems: 'baseline', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '10px 14px', fontSize: '14px' },
  pillOn: { background: 'var(--text)', color: 'var(--bg-card)', borderColor: 'var(--text)' },
  griglia: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '14px', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', display: 'block', width: '100%' },
  luogo: { color: 'var(--text-muted)', fontSize: '13px', marginTop: '4px', display: 'flex', gap: '4px', alignItems: 'center' },
  barra: { height: '6px', background: 'var(--bg-input)', borderRadius: '999px', overflow: 'hidden', margin: '12px 0 6px' },
  barraPiena: { height: '100%', background: '#15803D', borderRadius: '999px' },
  badge: { fontSize: '12px', fontWeight: 700, borderRadius: '999px', padding: '3px 10px', whiteSpace: 'nowrap', flexShrink: 0 },
  modif: { fontSize: '12px', color: '#B45309', fontWeight: 600 },
  vuoto: { display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'var(--text-dim)', padding: '40px 0', textAlign: 'center' },
  kpi: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', margin: '18px 0 8px' },
  kpiBox: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '14px' },
  tabella: { width: '100%', borderCollapse: 'collapse', background: 'var(--bg-card)', borderRadius: '14px', overflow: 'hidden' },
  th: { textAlign: 'left', fontSize: '12px', fontWeight: 700, letterSpacing: '.5px', color: 'var(--text-muted)', padding: '10px 12px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: '14px', verticalAlign: 'top' },
  velo: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', overflowY: 'auto' },
  finestra: { background: 'var(--bg-card)', borderRadius: '20px', padding: '22px', width: '100%', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,.25)', color: 'var(--text)' },
  h2: { fontSize: '20px', fontWeight: 700, margin: '4px 36px 10px 0', lineHeight: 1.25 },
  info: { display: 'flex', gap: '4px', alignItems: 'center', fontSize: '14px', color: 'var(--text-muted)' },
  nota: { fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '4px' },
  box: { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '14px', padding: '14px', marginTop: '14px' },
  avvisoBox: { display: 'flex', gap: '8px', background: '#FEF3C7', color: '#92400E', borderRadius: '12px', padding: '10px 12px', fontSize: '13.5px' },
  regole: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '14px 16px', marginBottom: '14px' },
  promemoria: { background: '#FFF7ED', border: '1px solid #FED7AA', color: '#9A3412', borderRadius: '12px', padding: '10px 12px', marginTop: '10px', fontSize: '14px' },
  chipNome: { border: '1px solid #FED7AA', background: '#fff', color: '#9A3412', borderRadius: '999px', padding: '4px 10px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' },
  daTess: { color: '#B45309', fontSize: '12px', fontWeight: 600 },
  senzaSconto: { color: '#6E6E73', fontWeight: 600 },
  rigaPag: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '14px' },
  etichetta: { fontSize: '12px', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '8px' },
  lab: { display: 'block', fontSize: '13px', fontWeight: 600, margin: '10px 0 4px' },
  input: { width: '100%', border: '1px solid var(--border2)', borderRadius: '12px', padding: '10px 12px', fontSize: '15px', color: 'var(--text)', background: 'var(--bg-card)', boxSizing: 'border-box' },
  xBtn: { border: 'none', background: 'var(--bg-input)', borderRadius: '999px', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' },
  primario: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#F4600C', color: '#fff', border: 'none', borderRadius: '999px', padding: '10px 18px', fontWeight: 700, cursor: 'pointer' },
  secondario: { display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text)', borderRadius: '999px', padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' },
  mini: { display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text)', borderRadius: '999px', padding: '5px 11px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' },
  miniNo: { display: 'inline-flex', alignItems: 'center', gap: '4px', border: 'none', background: '#FEE4E2', color: '#B42318', borderRadius: '999px', padding: '6px 12px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' },
  err: { color: '#B42318', fontSize: '14px', marginTop: '10px' },
  chiudiBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#15803D', color: '#fff', border: 'none', borderRadius: '999px', padding: '9px 16px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' },
  chiusoBox: { display: 'flex', gap: '10px', background: '#DCFCE7', color: '#14532D', borderRadius: '14px', padding: '12px 14px', marginTop: '16px', fontSize: '14px', lineHeight: 1.5 },
};
